import React from 'react'
import styled from 'styled-components'
import { useSessionContext } from 'components/sessions/sessionContext'
import PlayGamePlayer from 'components/play/Players/PlayGamePlayer'
import EvenlySpaced from 'shared/content-control/EvenlySpaced'
import { Button, PrimaryButton } from 'shared/Buttons'
import { One, Two, Three, Robot, CheckMark } from 'app/icons'
import GameContainer from 'components/play/GameContainer'

import {
  NavigationContextProvider,
  useNavigationContext,
} from './navigation-context'
import { useCreateGame } from 'components/games/queries'
import { navigate } from '@reach/router'

const GuestOnboardingWrapper = () => {
  return (
    <GameContainer>
      <NavigationContextProvider>
        <GuestOnboardingNavigation />
      </NavigationContextProvider>
    </GameContainer>
  )
}

const GuestOnboardingNavigation = () => {
  const { navigation, NAVIGATION_OPTIONS } = useNavigationContext()

  if (navigation === NAVIGATION_OPTIONS.HOME) return <Home />
  if (navigation === NAVIGATION_OPTIONS.DISCOVER_ONE) return <DiscoverOne />
  if (navigation === NAVIGATION_OPTIONS.DISCOVER_TWO) return <DiscoverTwo />
  if (navigation === NAVIGATION_OPTIONS.DISCOVER_THREE) return <DiscoverThree />
}

const Home = () => {
  const { setNavigation, NAVIGATION_OPTIONS } = useNavigationContext()
  const { session } = useSessionContext()
  const { username } = session?.user

  const [createGame] = useCreateGame()
  const [showErrorMessage, setShowErrorMessage] = React.useState(false)

  const createGameOnClick = async () => {
    const NUMBER_OF_ROUNDS = 1
    try {
      const { data } = await createGame({
        variables: { numberOfRounds: NUMBER_OF_ROUNDS },
      })
      if (data) navigate(`/play/${data.createdGame.id}`)
    } catch (e) {
      // Error occurred
      setShowErrorMessage(true)
    }
  }
  return (
    <>
      <EvenlySpaced.Column justifyContent="space-evenly">
        <div style={{ textAlign: 'center' }}>
          <p>Welcome, {username}!</p>
          <h1>Have you played golf before?</h1>
        </div>
        <DiscoverButton
          onClick={() => setNavigation(NAVIGATION_OPTIONS.DISCOVER_ONE)}
        />
        <Button isPrimary={true} onClick={createGameOnClick}>
          <IconWrapper>
            <Robot />
          </IconWrapper>
          Yes! Play a game now
        </Button>
        {showErrorMessage && <p>Something went wrong, please try again!</p>}
      </EvenlySpaced.Column>
      <PlayGamePlayer player={{ username, totalScore: null }} />
    </>
  )
}

const DiscoverButton = ({ onClick }) => (
  <Button isPrimary={true} onClick={onClick}>
    <IconWrapper>
      <One size={50} /> <Two size={50} /> <Three size={50} />
    </IconWrapper>
    No, discover in 3 steps...
  </Button>
)

const DiscoverOne = () => {
  const { setNavigation, NAVIGATION_OPTIONS } = useNavigationContext()
  const { username } = useSessionContext()

  const FADE_IN_START = 0.5
  const FADE_IN_INTERVAL = 2
  const FADE_IN_FINAL_INTERVAL = 3
  const FADE_IN_ITEM_1 = FADE_IN_START + FADE_IN_INTERVAL * 1
  const FADE_IN_ITEM_2 = FADE_IN_START + FADE_IN_INTERVAL * 2
  const FADE_IN_ITEM_3 = FADE_IN_START + FADE_IN_INTERVAL * 3
  const FADE_IN_ITEM_4 = FADE_IN_ITEM_3 + FADE_IN_FINAL_INTERVAL

  return (
    <>
      <EvenlySpaced.Column justifyContent="space-evenly">
        <>
          <Header />
          <FadeIn delayInSecs={FADE_IN_START}>
            <Title>
              <One /> Remember your cards
            </Title>
          </FadeIn>
          <FadeIn delayInSecs={FADE_IN_START}></FadeIn>
          <FadeInListItem delayInSecs={FADE_IN_ITEM_1}>
            <span>You get 4 cards face down</span>
          </FadeInListItem>
          <FadeInListItem delayInSecs={FADE_IN_ITEM_2}>
            <span>Pick any 2 cards to take a look at them</span>
          </FadeInListItem>
          <FadeInListItem delayInSecs={FADE_IN_ITEM_3}>
            <span>Remember your cards</span>
          </FadeInListItem>
        </>
        <FadeIn delayInSecs={FADE_IN_ITEM_4} style={{ textAlign: 'center' }}>
          <PrimaryButton
            onClick={() => setNavigation(NAVIGATION_OPTIONS.DISCOVER_TWO)}
            style={{ width: '5rem', marginLeft: 'auto' }}
          >
            Next
          </PrimaryButton>
        </FadeIn>
      </EvenlySpaced.Column>
      <PlayGamePlayer player={{ username, totalScore: null }} />
    </>
  )
}

const DiscoverTwo = () => {
  const { setNavigation, NAVIGATION_OPTIONS } = useNavigationContext()
  const { session } = useSessionContext()
  const { username } = session?.user

  const FADE_IN_START = 0.5
  const FADE_IN_INTERVAL = 2
  const FADE_IN_FINAL_INTERVAL = 3
  const FADE_IN_ITEM_1 = FADE_IN_START + FADE_IN_INTERVAL * 1
  const FADE_IN_ITEM_2 = FADE_IN_START + FADE_IN_INTERVAL * 2
  const FADE_IN_ITEM_3 = FADE_IN_START + FADE_IN_INTERVAL * 3
  const FADE_IN_ITEM_4 = FADE_IN_ITEM_3 + FADE_IN_FINAL_INTERVAL

  return (
    <>
      <EvenlySpaced.Column justifyContent="space-evenly">
        <>
          <Header />
          <FadeIn delayInSecs={FADE_IN_START}>
            <Title>
              <Two /> On your turn
            </Title>
          </FadeIn>
          <FadeIn delayInSecs={FADE_IN_START}></FadeIn>
          <FadeInListItem delayInSecs={FADE_IN_ITEM_1}>
            <span>Pick up a card, then keep, discard or reveal</span>
          </FadeInListItem>
          <FadeInListItem delayInSecs={FADE_IN_ITEM_2}>
            <span>Pairs cancel each other and score 0</span>
          </FadeInListItem>
          <FadeInListItem delayInSecs={FADE_IN_ITEM_3}>
            <span>Four of a kind scores -20</span>
          </FadeInListItem>
        </>
        <FadeIn delayInSecs={FADE_IN_ITEM_4} style={{ textAlign: 'center' }}>
          <PrimaryButton
            onClick={() => setNavigation(NAVIGATION_OPTIONS.DISCOVER_THREE)}
            style={{ width: '5rem', marginLeft: 'auto' }}
          >
            Next
          </PrimaryButton>
        </FadeIn>
      </EvenlySpaced.Column>
      <PlayGamePlayer player={{ username, totalScore: null }} />
    </>
  )
}

const DiscoverThree = () => {
  const { setNavigation, NAVIGATION_OPTIONS } = useNavigationContext()
  const { session } = useSessionContext()
  const { username } = session?.user

  const FADE_IN_START = 0.5
  const FADE_IN_INTERVAL = 2
  const FADE_IN_FINAL_INTERVAL = 3
  const FADE_IN_ITEM_1 = FADE_IN_START + FADE_IN_INTERVAL * 1
  const FADE_IN_ITEM_2 = FADE_IN_START + FADE_IN_INTERVAL * 2
  const FADE_IN_ITEM_3 = FADE_IN_START + FADE_IN_INTERVAL * 3
  const FADE_IN_ITEM_4 = FADE_IN_ITEM_3 + FADE_IN_FINAL_INTERVAL

  return (
    <>
      <EvenlySpaced.Column justifyContent="space-evenly">
        <>
          <Header />
          <FadeIn delayInSecs={FADE_IN_START}>
            <Title>
              <Three /> Score the least to win
            </Title>
          </FadeIn>
          <FadeIn delayInSecs={FADE_IN_START}></FadeIn>
          <FadeInListItem delayInSecs={FADE_IN_ITEM_1}>
            <span>Cards score their number</span>
          </FadeInListItem>
          <FadeInListItem delayInSecs={FADE_IN_ITEM_2}>
            <span>Q and J score 10</span>
          </FadeInListItem>
          <FadeInListItem delayInSecs={FADE_IN_ITEM_3}>
            <span>K scores 0</span>
          </FadeInListItem>
        </>
        <FadeIn delayInSecs={FADE_IN_ITEM_4} style={{ textAlign: 'center' }}>
          <PrimaryButton
            onClick={() => setNavigation(NAVIGATION_OPTIONS.HOME)}
            style={{ width: '5rem', marginLeft: 'auto' }}
          >
            Next
          </PrimaryButton>
        </FadeIn>
      </EvenlySpaced.Column>
      <PlayGamePlayer player={{ username, totalScore: null }} />
    </>
  )
}

const Header = () => <p style={{ textAlign: 'center' }}>How to play</p>
const FadeInListItem = ({ children, delayInSecs }) => (
  <FadeIn delayInSecs={delayInSecs}>
    <EvenlySpaced.Row style={{ padding: '1rem', justifyContent: 'flex-start' }}>
      <CheckMark style={{ marginRight: '2rem', width: '1rem' }} /> {children}
    </EvenlySpaced.Row>
  </FadeIn>
)
const Title = ({ children }) => (
  <h1 style={{ textAlign: 'center' }}>{children}</h1>
)

const FadeIn = styled.div`
  -webkit-animation: 3s ease 0s normal forwards 1 fadein;
  animation: ${(props) => props.delayInSecs}s ease 0s normal forwards 1 fadein;

  @keyframes fadein {
    0% {
      opacity: 0;
    }
    66% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @-webkit-keyframes fadein {
    0% {
      opacity: 0;
    }
    66% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`

const IconWrapper = styled(EvenlySpaced.Row)`
  height: 3rem;
  margin: 1rem 0;
  justify-content: center;
`

export default GuestOnboardingWrapper
